import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { ApiComponents } from '@aztec-realms/api-client';

import { browserApiClient, requiredApiData } from './data/browser-api';
import { verticalSliceQueryKey } from './data/use-vertical-slice';

export type AvatarCatalog = ApiComponents['schemas']['AvatarCatalogResponse'];

const avatarCatalogQueryKey = ['player', 'avatar-catalog', 'v1'] as const;

async function loadAvatarCatalog(): Promise<AvatarCatalog> {
  const result = await browserApiClient().GET('/api/v1/player/avatars');
  return requiredApiData(result.data, result.error, 'Avatar catalogue is unavailable.');
}

async function selectAvatar(assetId: string): Promise<void> {
  const result = await browserApiClient({ mutation: true }).PUT('/api/v1/player/avatar', {
    body: { asset_id: assetId },
  });
  requiredApiData(result.data, result.error, 'Avatar selection could not be saved.');
}

export function AvatarSettings({
  online,
  loadCatalog = loadAvatarCatalog,
  saveAvatar = selectAvatar,
  fixtureCatalog,
}: {
  online: boolean;
  loadCatalog?: () => Promise<AvatarCatalog>;
  saveAvatar?: (assetId: string) => Promise<void>;
  fixtureCatalog?: AvatarCatalog;
}) {
  const queryClient = useQueryClient();
  const catalog = useQuery({
    queryKey: avatarCatalogQueryKey,
    queryFn: loadCatalog,
    enabled: online && !fixtureCatalog,
    initialData: fixtureCatalog,
  });
  const selection = useMutation({
    mutationFn: saveAvatar,
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: avatarCatalogQueryKey }),
        queryClient.invalidateQueries({ queryKey: verticalSliceQueryKey }),
      ]);
    },
  });

  return (
    <div className="avatar-settings">
      <div className="avatar-settings__heading">
        <div>
          <span className="realm-screen__eyebrow">Player identity</span>
          <h2>Choose who enters the realm</h2>
        </div>
        {catalog.data && <span>Account level {catalog.data.level} of 40</span>}
      </div>
      <p>
        New identities unlock through play. They change presentation only—not rewards, mastery or
        rank.
      </p>

      {!online && (
        <p className="inline-error" role="status">
          Reconnect to review or change your server-owned profile.
        </p>
      )}
      {catalog.isPending && online && (
        <div aria-busy="true" className="avatar-settings__loading" role="status">
          Restoring avatar choices…
        </div>
      )}
      {catalog.isError && (
        <p className="inline-error" role="alert">
          Avatar choices are temporarily unavailable.
        </p>
      )}
      {selection.isError && (
        <p className="inline-error" role="alert">
          {selection.error instanceof Error
            ? selection.error.message
            : 'Avatar selection could not be saved.'}
        </p>
      )}

      {catalog.data && (
        <div aria-label="Available player identities" className="avatar-settings__grid">
          {catalog.data.avatars.map((avatar) => (
            <button
              aria-pressed={avatar.selected}
              className="avatar-choice"
              data-locked={!avatar.unlocked || undefined}
              disabled={!avatar.unlocked || selection.isPending}
              key={avatar.asset_id}
              onClick={() => selection.mutate(avatar.asset_id)}
              type="button"
            >
              <span className="avatar-choice__portrait">
                <img alt="" height="96" loading="lazy" src={avatar.asset_url} width="96" />
                <span>{avatar.selected ? 'Selected' : `Level ${avatar.unlock_level}`}</span>
              </span>
              <span className="avatar-choice__copy">
                <strong>{avatar.label}</strong>
                <small>{avatar.description}</small>
                <small>
                  {avatar.unlocked ? 'Unlocked through play' : 'Keep exploring to unlock'}
                </small>
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
